import { adminSupabase } from './supabaseService';
import { userService } from './userService';
import { getWalletProfitAndLoss } from './solanatrackerService';
import { jupiterService } from './jupiterService';

export interface SniperPosition {
    id: string;
    user_id: string;
    rule_id: string;
    rule_name: string;
    token_address: string;
    token_symbol?: string;
    token_name?: string;
    trading_wallet_address: string;
    entry_price?: number;
    entry_amount_sol: number;
    current_amount_tokens?: number;
    snipe_transaction_signature: string;
    status: 'active' | 'auto_sold' | 'manually_sold' | 'failed' | 'auto_selling';
    auto_sell_config?: {
        enabled: boolean;
        profitPercent: number;
        lossPercent: number;
        positionPercent: number;
    };
    profit_loss_percent?: number;
    current_value_sol?: number;
    auto_sell_transaction_signature?: string;
    created_at: string;
    updated_at: string;
    auto_sold_at?: string;
}

export interface SniperPositionUpdate {
    profit_loss_percent?: number;
    current_value_sol?: number;
    current_amount_tokens?: number;
    status?: 'active' | 'auto_sold' | 'manually_sold' | 'failed' | 'auto_selling';
    auto_sell_transaction_signature?: string;
    auto_sold_at?: string;
}

export const sniperPositionsService = {
    /**
     * Create a new sniper position record
     */
    async createSniperPosition(
        walletAddress: string,
        ruleId: string,
        ruleName: string,
        tokenAddress: string,
        tokenSymbol: string,
        tokenName: string,
        tradingWalletAddress: string,
        entryAmountSol: number,
        snipeTransactionSignature: string,
        autoSellConfig?: {
            enabled: boolean;
            profitPercent: number;
            lossPercent: number;
            positionPercent: number;
        }
    ): Promise<SniperPosition | null> {
        try {
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            const { data: position, error } = await adminSupabase
                .from('sniper_positions')
                .insert({
                    user_id: user.id,
                    rule_id: ruleId,
                    rule_name: ruleName,
                    token_address: tokenAddress,
                    token_symbol: tokenSymbol,
                    token_name: tokenName,
                    trading_wallet_address: tradingWalletAddress,
                    entry_amount_sol: entryAmountSol,
                    snipe_transaction_signature: snipeTransactionSignature,
                    status: 'active',
                    auto_sell_config: autoSellConfig
                })
                .select()
                .single();

            if (error) {
                console.error('Error creating sniper position:', error);
                return null;
            }

            console.log(`✅ Created sniper position record for ${tokenSymbol}`);
            return position;
        } catch (error) {
            console.error('Error in createSniperPosition:', error);
            return null;
        }
    },

    /**
     * Get active sniper positions for a user
     */
    async getUserSniperPositions(walletAddress: string): Promise<SniperPosition[]> {
        try {
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) return [];

            const { data: positions, error } = await adminSupabase
                .from('sniper_positions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching sniper positions:', error);
                return [];
            }

            return positions || [];
        } catch (error) {
            console.error('Error in getUserSniperPositions:', error);
            return [];
        }
    },

    /**
     * Update a sniper position
     */
    async updateSniperPosition(
        positionId: string,
        updates: SniperPositionUpdate
    ): Promise<boolean> {
        try {
            const { error } = await adminSupabase
                .from('sniper_positions')
                .update({
                    ...updates,
                    updated_at: new Date().toISOString()
                })
                .eq('id', positionId);

            if (error) {
                console.error('Error updating sniper position:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error in updateSniperPosition:', error);
            return false;
        }
    },

    /**
     * Monitor positions and execute auto-sells
     */
    async monitorAndAutoSell(walletAddress: string): Promise<void> {
        try {
            const positions = await this.getUserSniperPositions(walletAddress);
            if (positions.length === 0) {
                console.log('📍 No active sniper positions to monitor');
                return;
            }

            console.log(`🔍 Monitoring ${positions.length} sniper positions for auto-sell...`);

            // Get all unique trading wallet addresses from positions
            const tradingWallets = [...new Set(positions.map(p => p.trading_wallet_address))];
            console.log(`📋 Need P&L data for ${tradingWallets.length} trading wallets:`, tradingWallets.map(w => w.slice(0, 8) + '...'));

            // Get P&L data for each trading wallet since positions might be in different wallets
            const allTokensData: any = {};

            for (const tradingWallet of tradingWallets) {
                console.log(`📊 Fetching P&L data for trading wallet ${tradingWallet.slice(0, 8)}...`);

                const pnlResponse = await getWalletProfitAndLoss(tradingWallet);
                if (pnlResponse.status === 'success' && pnlResponse.data) {
                    const tokensData = pnlResponse.data.tokens || {};
                    console.log(`  ✅ Found ${Object.keys(tokensData).length} tokens in wallet ${tradingWallet.slice(0, 8)}...`);

                    // Merge tokens data with wallet source info
                    Object.keys(tokensData).forEach(tokenAddress => {
                        allTokensData[tokenAddress] = {
                            ...(tokensData as any)[tokenAddress],
                            source_wallet: tradingWallet
                        };
                    });
                } else {
                    console.warn(`  ❌ Could not fetch P&L data for wallet ${tradingWallet.slice(0, 8)}...`);
                }
            }

            console.log(`📊 Combined P&L data for ${Object.keys(allTokensData).length} total tokens across all wallets`);

            for (const position of positions) {
                console.log(`\n🔍 Checking position: ${position.token_symbol} (${position.token_address})`);

                // Skip if auto-sell is not enabled for this position
                if (!position.auto_sell_config?.enabled) {
                    console.log(`⏭️  Auto-sell disabled for ${position.token_symbol}`);
                    continue;
                }

                console.log(`✅ Auto-sell enabled for ${position.token_symbol}:`, {
                    profitPercent: position.auto_sell_config.profitPercent,
                    lossPercent: position.auto_sell_config.lossPercent,
                    positionPercent: position.auto_sell_config.positionPercent
                });

                // Get token P&L data
                const tokenPnL = allTokensData[position.token_address];
                if (!tokenPnL) {
                    console.warn(`❌ No P&L data found for token ${position.token_symbol} (${position.token_address})`);
                    console.log('Available tokens in P&L:', Object.keys(allTokensData));

                    // Also check if token might be in a different wallet
                    console.log(`🔍 Checking if ${position.token_symbol} might be in a different wallet...`);
                    for (const tradingWallet of tradingWallets) {
                        const walletPnlResponse = await getWalletProfitAndLoss(tradingWallet);
                        if (walletPnlResponse.status === 'success' && walletPnlResponse.data) {
                            const walletTokens = Object.keys(walletPnlResponse.data.tokens || {});
                            if (walletTokens.includes(position.token_address)) {
                                console.log(`  ✅ Found ${position.token_symbol} in wallet ${tradingWallet.slice(0, 8)}...`);
                                break;
                            }
                        }
                    }
                    continue;
                }

                console.log(`📈 P&L data for ${position.token_symbol} (found in wallet ${tokenPnL.source_wallet?.slice(0, 8)}...):`, {
                    current_value: tokenPnL.current_value,
                    total_invested: tokenPnL.total_invested,
                    holding: tokenPnL.holding,
                    token_address: position.token_address
                });

                // Calculate profit/loss percentage
                const profitLossPercent = tokenPnL.total_invested > 0
                    ? ((tokenPnL.current_value - tokenPnL.total_invested) / tokenPnL.total_invested) * 100
                    : 0;

                console.log(`💰 Calculated profit/loss: ${profitLossPercent.toFixed(2)}%`);

                // Update position with current data
                await this.updateSniperPosition(position.id, {
                    profit_loss_percent: profitLossPercent,
                    current_value_sol: tokenPnL.current_value,
                    current_amount_tokens: tokenPnL.holding
                });

                // Check if auto-sell conditions are met
                const { profitPercent, lossPercent, positionPercent } = position.auto_sell_config;

                let shouldSell = false;
                let sellReason = '';

                console.log(`🎯 Checking auto-sell conditions:`);
                console.log(`   Current P&L: ${profitLossPercent.toFixed(2)}%`);
                console.log(`   Profit target: +${profitPercent}%`);
                console.log(`   Loss limit: -${lossPercent}%`);

                if (profitLossPercent >= profitPercent) {
                    shouldSell = true;
                    sellReason = `profit target (+${profitLossPercent.toFixed(2)}% >= +${profitPercent}%)`;
                    console.log(`✅ PROFIT TARGET HIT: ${sellReason}`);
                } else if (profitLossPercent <= -Math.abs(lossPercent)) {
                    shouldSell = true;
                    sellReason = `stop loss (${profitLossPercent.toFixed(2)}% <= -${lossPercent}%)`;
                    console.log(`🛑 STOP LOSS HIT: ${sellReason}`);
                } else {
                    console.log(`⏳ No auto-sell conditions met for ${position.token_symbol}`);
                }

                if (shouldSell) {
                    console.log(`🎯 AUTO-SELL TRIGGERED IMMEDIATELY for ${position.token_symbol}: ${sellReason}`);

                    // Mark position as auto_selling to prevent multiple attempts
                    await this.updateSniperPosition(position.id, {
                        status: 'auto_selling'
                    });

                    // Execute auto-sell once only
                    const sellSuccess = await this.executeAutoSell(position, positionPercent, sellReason);

                    if (sellSuccess) {
                        console.log(`✅ Auto-sell completed successfully for ${position.token_symbol}`);
                    } else {
                        console.log(`❌ Auto-sell failed for ${position.token_symbol}, marking as failed`);
                        // Mark as failed to prevent further attempts
                        await this.updateSniperPosition(position.id, {
                            status: 'failed'
                        });
                    }
                } else {
                    console.log(`📊 ${position.token_symbol}: ${profitLossPercent.toFixed(2)}% (target: ${profitPercent >= 0 ? '+' : ''}${profitPercent}%)`);
                }
            }
        } catch (error) {
            console.error('❌ Error in monitorAndAutoSell:', error);
        }
    },

    /**
 * Execute auto-sell for a position - SINGLE ATTEMPT ONLY
 */
    async executeAutoSell(
        position: SniperPosition,
        positionPercent: number,
        reason: string
    ): Promise<boolean> {
        try {
            console.log(`🤖 SINGLE AUTO-SELL ATTEMPT for ${position.token_symbol} (${positionPercent}% of position)`);
            console.log(`   Token: ${position.token_address}`);
            console.log(`   Trading Wallet: ${position.trading_wallet_address}`);
            console.log(`   Entry Amount: ${position.entry_amount_sol} SOL`);
            console.log(`   Position Percent to Sell: ${positionPercent}%`);

            // Calculate amount to sell based on entry amount and position percent (from auto-sell rules)
            const entryAmountSol = position.entry_amount_sol || 0;
            const solAmountToSell = entryAmountSol * (positionPercent / 100);

            if (solAmountToSell <= 0) {
                console.warn(`❌ Invalid SOL amount to sell: ${solAmountToSell} (entry: ${entryAmountSol}, percent: ${positionPercent}%)`);
                return false;
            }

            console.log(`💰 Selling ${positionPercent}% of ${entryAmountSol} SOL position = ${solAmountToSell} SOL worth of ${position.token_symbol}`);

            // Import trade engine
            const { EkkoTradeEngine } = await import('./tradeEngine');
            const tradeEngine = EkkoTradeEngine.getInstance();

            // For auto-sell, we'll use the trading wallet as both user and trading wallet
            // This simplifies the trade engine call and avoids user lookup issues
            const userWalletAddress = position.trading_wallet_address;
            console.log(`👤 User wallet: ${userWalletAddress.slice(0, 8)}...`);

            // Execute sell using trade engine (Token -> SOL)
            console.log(`🔄 Using EKKO Trade Engine to sell ${position.token_symbol} for SOL...`);

            const tradeResult = await tradeEngine.executeSwap({
                inputMint: position.token_address,
                outputMint: 'So11111111111111111111111111111111111111112', // SOL mint
                amount: solAmountToSell.toString(), // Amount in SOL equivalent
                userWalletAddress: userWalletAddress,
                tradingWalletAddress: position.trading_wallet_address,
                slippagePercent: '20', // 20% slippage for auto-sells
                tradeContext: {
                    isSniper: true,
                    ruleId: position.rule_id,
                    ruleName: position.rule_name,
                    metadata: {
                        autoSell: true,
                        reason: reason,
                        positionPercent: positionPercent
                    }
                }
            });

            if (tradeResult.signature) {
                // Update position status - mark as auto_sold if 100%, otherwise keep active
                await this.updateSniperPosition(position.id, {
                    status: positionPercent === 100 ? 'auto_sold' : 'active',
                    auto_sell_transaction_signature: tradeResult.signature,
                    auto_sold_at: new Date().toISOString()
                });

                console.log(`✅ Auto-sell completed for ${position.token_symbol}: ${tradeResult.signature}`);

                // Show notification
                this.showAutoSellNotification(position, positionPercent, reason, tradeResult.signature);

                return true;
            } else {
                console.error(`❌ Auto-sell failed for ${position.token_symbol}: No transaction signature returned`);
                return false;
            }
        } catch (error) {
            console.error('❌ Error in executeAutoSell:', error);

            // Check for specific errors that indicate we should stop trying
            const errorMsg = error instanceof Error ? error.message : String(error);
            if (errorMsg.includes('TOKEN_NOT_TRADABLE') ||
                errorMsg.includes('Pool is completed') ||
                errorMsg.includes('TOKEN_METADATA_MISSING') ||
                errorMsg.includes('INSUFFICIENT_BALANCE')) {
                console.log(`🛑 Token ${position.token_symbol} has permanent error, will be marked as failed by caller`);
            }

            return false;
        }
    },

    /**
     * Show auto-sell notification
     */
    showAutoSellNotification(
        position: SniperPosition,
        positionPercent: number,
        reason: string,
        txId: string
    ): void {
        const message = `Auto-sold ${positionPercent}% of ${position.token_symbol} (${reason})`;

        window.dispatchEvent(new CustomEvent('ekko:show_toast', {
            detail: {
                message,
                type: 'success',
                id: `auto-sell-${position.id}`,
                txId,
                duration: 10000 // Show for 10 seconds
            }
        }));
    },

    /**
     * Start monitoring positions (INDEPENDENT of sniper bot status)
     */
    startPositionMonitoring(walletAddress: string): () => void {
        console.log(`🤖 Starting INDEPENDENT sniper position monitoring for ${walletAddress.slice(0, 8)}...`);
        console.log(`📋 This monitoring continues even when sniper bot is inactive to handle existing auto-sell positions`);

        const interval = setInterval(async () => {
            await this.monitorAndAutoSell(walletAddress);

            // Check if there are still active positions to monitor
            const hasActivePositions = await this.hasActiveAutoSellPositions(walletAddress);
            if (!hasActivePositions) {
                console.log(`📊 No active auto-sell positions found, but continuing monitoring for new positions...`);
            } else {
                console.log(`✅ Found active auto-sell positions, continuing monitoring...`);
            }
        }, 1000); // Check every 1 second for immediate response

        // Return cleanup function
        return () => {
            console.log(`⏹️ Stopping INDEPENDENT sniper position monitoring`);
            clearInterval(interval);
        };
    },

    /**
     * Check if there are active positions with auto-sell enabled
     */
    async hasActiveAutoSellPositions(walletAddress: string): Promise<boolean> {
        try {
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) return false;

            const { data: positions, error } = await adminSupabase
                .from('sniper_positions')
                .select('auto_sell_config, status')
                .eq('user_id', user.id)
                .in('status', ['active', 'auto_selling']); // Include both active and currently selling positions

            if (error) {
                console.error('Error checking active auto-sell positions:', error);
                return false;
            }

            return (positions || []).some(position =>
                position.auto_sell_config?.enabled === true
            );
        } catch (error) {
            console.error('Error in hasActiveAutoSellPositions:', error);
            return false;
        }
    },

    /**
     * Get auto-sell transactions for display in SniperBot
     */
    async getAutoSellTransactions(walletAddress: string): Promise<any[]> {
        try {
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) return [];

            const { data: positions, error } = await adminSupabase
                .from('sniper_positions')
                .select('*')
                .eq('user_id', user.id)
                .not('auto_sell_transaction_signature', 'is', null)
                .order('auto_sold_at', { ascending: false })
                .limit(50);

            if (error) {
                console.error('Error fetching auto-sell transactions:', error);
                return [];
            }

            // Format as transactions for display
            return (positions || []).map(position => ({
                id: `auto-sell-${position.id}`,
                type: 'auto_sell',
                status: 'success',
                token_address: position.token_address,
                token_symbol: position.token_symbol,
                token_name: position.token_name,
                amount_sol: position.current_value_sol || 0,
                transaction_signature: position.auto_sell_transaction_signature,
                created_at: position.auto_sold_at,
                rule_name: position.rule_name,
                profit_loss_percent: position.profit_loss_percent,
                entry_amount_sol: position.entry_amount_sol
            }));
        } catch (error) {
            console.error('Error in getAutoSellTransactions:', error);
            return [];
        }
    },

    /**
     * Debug function to check a specific position's auto-sell status
     */
    async debugAutoSellPosition(walletAddress: string, tokenAddress?: string): Promise<void> {
        try {
            console.log(`🔧 DEBUG: Auto-sell check for wallet ${walletAddress.slice(0, 8)}...`);

            const positions = await this.getUserSniperPositions(walletAddress);
            console.log(`📍 Found ${positions.length} active positions`);

            if (tokenAddress) {
                const position = positions.find(p => p.token_address.toLowerCase() === tokenAddress.toLowerCase());
                if (position) {
                    console.log(`🎯 Found specific position for ${tokenAddress}:`, position);
                } else {
                    console.log(`❌ No position found for token ${tokenAddress}`);
                    console.log('Available positions:', positions.map(p => ({
                        symbol: p.token_symbol,
                        address: p.token_address,
                        auto_sell_enabled: p.auto_sell_config?.enabled
                    })));
                }
            } else {
                console.log('All positions:', positions.map(p => ({
                    symbol: p.token_symbol,
                    address: p.token_address,
                    auto_sell_enabled: p.auto_sell_config?.enabled,
                    auto_sell_config: p.auto_sell_config
                })));
            }

            // Force run the monitoring once
            console.log('🔄 Running monitoring check now...');
            await this.monitorAndAutoSell(walletAddress);

        } catch (error) {
            console.error('❌ Debug error:', error);
        }
    },

    /**
     * Debug function to manually create a position for existing trade
     */
    async debugCreatePosition(
        walletAddress: string,
        tokenAddress: string,
        tokenSymbol: string,
        profitPercent: number = 20,
        lossPercent: number = 25
    ): Promise<void> {
        try {
            console.log(`🔧 DEBUG: Creating manual position for ${tokenSymbol} (${tokenAddress})`);

            const position = await this.createSniperPosition(
                walletAddress,
                'debug-rule',
                'Debug Rule',
                tokenAddress,
                tokenSymbol,
                tokenSymbol,
                walletAddress, // Using main wallet as trading wallet
                0.1, // Default entry amount
                'debug-tx',
                {
                    enabled: true,
                    profitPercent: profitPercent,
                    lossPercent: lossPercent,
                    positionPercent: 100
                }
            );

            if (position) {
                console.log(`✅ Created debug position for ${tokenSymbol}:`, position);

                // Start monitoring immediately
                console.log('🔄 Starting monitoring for debug position...');
                await this.monitorAndAutoSell(walletAddress);
            } else {
                console.error(`❌ Failed to create debug position for ${tokenSymbol}`);
            }
        } catch (error) {
            console.error('❌ Debug create position error:', error);
        }
    },

    /**
     * Force trigger monitoring right now for debugging
     */
    async debugForceMonitoring(walletAddress: string): Promise<void> {
        console.log('🔧 DEBUG: Force triggering monitoring now...');
        await this.monitorAndAutoSell(walletAddress);
        console.log('✅ DEBUG: Manual monitoring completed');
    },
};

// Add to global window for debugging
if (typeof window !== 'undefined') {
    (window as any).debugAutoSell = sniperPositionsService.debugAutoSellPosition.bind(sniperPositionsService);
    (window as any).createDebugPosition = sniperPositionsService.debugCreatePosition.bind(sniperPositionsService);
    (window as any).forceMonitoring = sniperPositionsService.debugForceMonitoring.bind(sniperPositionsService);
    (window as any).checkAutoSellStatus = async (walletAddress: string) => {
        console.log('🔧 CHECKING AUTO-SELL STATUS...');
        const positions = await sniperPositionsService.getUserSniperPositions(walletAddress);
        console.log(`📍 Found ${positions.length} positions:`);
        positions.forEach(pos => {
            console.log(`  ${pos.token_symbol}: ${pos.status}, auto-sell: ${pos.auto_sell_config?.enabled}, profit target: ${pos.auto_sell_config?.profitPercent}%`);
        });
        return positions;
    };
} 